const MAX_INPUT_BYTES = 8 * 1024 * 1024;
const MAX_OUTPUT_BYTES = 200 * 1024;
const MAX_DIMENSION = 256;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read the image file."));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not process the image."));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });
}

/**
 * Downscale and compress avatar uploads for small display sizes (20–64px).
 * Returns a JPEG blob suitable for agent list/chat surfaces.
 */
export async function prepareAvatarUploadFile(file) {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Choose a PNG, JPG, WEBP, or GIF image.");
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error("Image must be 8 MB or smaller.");
  }

  const image = await loadImageFromFile(file);
  const scale = Math.min(
    1,
    MAX_DIMENSION / Math.max(image.naturalWidth || 1, image.naturalHeight || 1)
  );
  const width = Math.max(1, Math.round((image.naturalWidth || 1) * scale));
  const height = Math.max(1, Math.round((image.naturalHeight || 1) * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not process the image.");
  }
  context.drawImage(image, 0, 0, width, height);

  let quality = 0.86;
  let blob = await canvasToBlob(canvas, "image/jpeg", quality);
  while (blob.size > MAX_OUTPUT_BYTES && quality > 0.5) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, "image/jpeg", quality);
  }

  if (blob.size > MAX_OUTPUT_BYTES) {
    throw new Error(
      "Image is too detailed to compress for an avatar. Try a simpler photo."
    );
  }

  const baseName = String(file.name || "avatar")
    .replace(/\.[^.]+$/, "")
    .trim();
  const safeName = baseName ? `${baseName}.jpg` : "avatar.jpg";
  return new File([blob], safeName, { type: "image/jpeg" });
}
